import Cookies from 'js-cookie';

/**
 * Pinned to the apex domain so cookies flow to every subdomain (api.
 * runs on Cloud Run and won't receive these without the worker, but
 * www. / app. / staging. all do). Matches the Cloudflare attribution
 * worker (`apps/worker/src/cookies.ts`) so the FE-fallback writes
 * collide-by-name with the worker output and the first-touch dedup
 * works across both writers.
 */
export const ATTRIBUTION_COOKIE_DOMAIN = '.fantasyfootballhub.co.uk';
export const ATTRIBUTION_COOKIE_EXPIRES_DAYS = 30;

export const UTM_FIRST_TOUCH_COOKIE = 'utm_first_touch';
export const UTM_LAST_TOUCH_COOKIE = 'utm_last_touch';

/**
 * Click IDs the FE can capture from the URL as a fallback when the
 * `go.` Cloudflare worker has not run (affiliate links, WhatsApp,
 * Outlook Safe Links). Same set as `apps/worker/src/constants.ts`
 * `CLICK_ID_KEYS` — order is not load-bearing on either side, each
 * key is written/read independently. `_fbc` / `_fbp` are excluded
 * because the Facebook pixel SDK owns those.
 */
export const CLICK_ID_KEYS = [
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
  'sc_click_id'
] as const;

export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

/**
 * Cookie JSON shape — matches the worker's payload (`utm_*` prefixed
 * keys, URL-encoded JSON). The backend `utmDict` schema uses BARE
 * keys (`source`, `medium`, …); `toBareUtm` does the remap.
 */
export type UtmCookieValue = Partial<Record<UtmKey, string>>;

export type BareUtm = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
};

export type CapturedAttribution = {
  utm: UtmCookieValue;
  clickIds: Partial<Record<ClickIdKey, string>>;
};

const cookieOptions = {
  domain: ATTRIBUTION_COOKIE_DOMAIN,
  path: '/',
  expires: ATTRIBUTION_COOKIE_EXPIRES_DAYS,
  secure: true,
  sameSite: 'lax' as const
};

/**
 * Backend `utmDict` (apps/api/src/modules/billing/schemas.ts) caps every
 * value at 256 chars. The worker truncates to the same length in
 * `apps/worker/src/attribution.ts` so worker-written cookies always
 * fit. The FE fallback writer must match — without this, an oversized
 * UTM on a direct hit (long affiliate `?utm_campaign=…`) lands in the
 * cookie unchanged and a subsequent /billing/checkout POST 400s on
 * the very edge case the fallback exists to handle.
 */
const VALUE_MAX_LENGTH = 256;

const sanitiseValue = (raw: string | null | undefined): string | undefined => {
  if (raw === undefined || raw === null) return undefined;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.length > VALUE_MAX_LENGTH
    ? trimmed.slice(0, VALUE_MAX_LENGTH)
    : trimmed;
};

const isNonEmpty = (value: string | undefined | null): value is string =>
  typeof value === 'string' && value.length > 0;

export const readUtmCookie = (
  name: typeof UTM_FIRST_TOUCH_COOKIE | typeof UTM_LAST_TOUCH_COOKIE
): UtmCookieValue | null => {
  const raw = Cookies.get(name);
  if (!isNonEmpty(raw)) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const result: UtmCookieValue = {};
    for (const key of UTM_KEYS) {
      const value = record[key];
      // Sanitise on read too: a stale cookie written before the
      // FE-fallback truncation landed (or hand-crafted) could still
      // carry an oversized value that would 400 the checkout via the
      // backend's max(256) cap.
      const sanitised =
        typeof value === 'string' ? sanitiseValue(value) : undefined;
      if (sanitised !== undefined) result[key] = sanitised;
    }
    return result;
  } catch {
    // Malformed cookie (e.g. a stray non-JSON value) — fail soft so a
    // bad cookie never breaks the checkout body builder.
    return null;
  }
};

export const toBareUtm = (
  parsed: UtmCookieValue | null
): BareUtm | undefined => {
  if (!parsed) return undefined;
  const bare: BareUtm = {};
  if (parsed.utm_source) bare.source = parsed.utm_source;
  if (parsed.utm_medium) bare.medium = parsed.utm_medium;
  if (parsed.utm_campaign) bare.campaign = parsed.utm_campaign;
  if (parsed.utm_content) bare.content = parsed.utm_content;
  if (parsed.utm_term) bare.term = parsed.utm_term;
  return Object.keys(bare).length === 0 ? undefined : bare;
};

/**
 * Write a UTM/click-ID cookie pinned to the apex domain.
 *
 * `firstTouch: true` is a no-op if the cookie already exists — the
 * first-touch invariant must survive across multiple navigations,
 * the FE-fallback writer, AND the worker writer.
 */
export const writeAttributionCookie = (
  name: string,
  value: string,
  options: { firstTouch?: boolean } = {}
): void => {
  if (options.firstTouch && Cookies.get(name) !== undefined) {
    return;
  }
  Cookies.set(name, value, cookieOptions);
};

export const captureFromUrl = (search: string): CapturedAttribution => {
  const params = new URLSearchParams(search);
  const utm: UtmCookieValue = {};
  for (const key of UTM_KEYS) {
    const sanitised = sanitiseValue(params.get(key));
    if (sanitised !== undefined) utm[key] = sanitised;
  }
  const clickIds: Partial<Record<ClickIdKey, string>> = {};
  for (const key of CLICK_ID_KEYS) {
    const sanitised = sanitiseValue(params.get(key));
    if (sanitised !== undefined) clickIds[key] = sanitised;
  }
  return { utm, clickIds };
};

/**
 * Apply a freshly-parsed URL capture to first-party cookies.
 *
 * UTM rules: first-touch is write-once across all touches, last-touch
 * overwrites on every navigation that brings new UTM data. Click IDs
 * overwrite on every touch (matches worker behaviour — there is no
 * first/last-touch click ID).
 */
export const persistCapturedAttribution = ({
  utm,
  clickIds
}: CapturedAttribution): void => {
  for (const [key, value] of Object.entries(clickIds)) {
    if (isNonEmpty(value)) writeAttributionCookie(key, value);
  }
  if (Object.keys(utm).length > 0) {
    const json = JSON.stringify(utm);
    writeAttributionCookie(UTM_LAST_TOUCH_COOKIE, json);
    writeAttributionCookie(UTM_FIRST_TOUCH_COOKIE, json, { firstTouch: true });
  }
};
